import { ahuBoardApi } from 'app/services/ahuBoardApi';

/**
 * Phase 6e2c-2 — EVAP PACK selector data layer (§33.9).
 *
 * The selector reuses the COMPONENT-FORM GET endpoints for read-only loads:
 *  - staging snapshot:  GET ahu/evap_packs/<id>   (reuse getComponentDetail —
 *    {plural:'evap_packs', id}; returns the 5 params blobs + the
 *    *_automatic_valve_ids arrays + eadb/eawb/media_aw_* + all output fields).
 *  - CMS doc:           GET cms/1/v1.0.0/ahu/evap_pack/params/  (reuse
 *    getComponentCmsDoc — SINGULAR evap_pack route; wrapped under "evap_pack").
 *
 * This file adds ONLY the live-preview PATCH (the §33.9 persist-on-change +
 * the cancel rollback). ⚠ DATA-SAFETY: this mutation WRITES to the shared dev3
 * DB. It is wired behind the selector's userArmed change-gate + 500 ms debounce
 * (legacy _scheduleUpdate → _saveAndUpdate) and the Cancel rollback. It is NEVER
 * fired on mount / open / tab-switch / cancel-without-edits. In smoke testing
 * the persist path is exercised by CODE REVIEW only (never against live data).
 *
 * Legacy payload (evap_pack.selector.js _saveAndUpdate / cancelChanges):
 *   data = staging_model.toJSON(); delete data.id;
 *   fn.uni_tools.patch('/api/v1.0/ahu/evap_packs/<id>/', data, {keepUrl:true, retries:1})
 * The legacy URL carries a trailing slash + keepUrl (bypasses standardizeURL);
 * the actual DRF detail route is `^evap_packs/(?P<pk>[0-9]+)$` (slashless — same
 * route getComponentDetail GETs). We PATCH the slashless form so it resolves;
 * the body shape is byte-identical to legacy (full staging JSON sans id).
 */

/** The full evap_pack staging row (loose — the tmpl reads ~50 fields). */
export type IEvapPackRow = Record<string, unknown>;

/** The ten fields `_fetchCalculate` writes back (evap_pack.selector.js:986-996). */
export type IEvapCalculateResult = {
  media_aw_width?: number | null;
  media_aw_height?: number | null;
  sump_length?: number | null;
  face_velocity_actual?: number | null;
  face_velocity_std?: number | null;
  airflow_std?: number | null;
  media_apd?: number | null;
  media_clg_eff?: number | null;
  media_clg_eff_percent?: number | null;
  water_flow?: number | null;
};

export const evapPackSelectorApi = ahuBoardApi.injectEndpoints({
  endpoints: (builder) => ({
    // Live-preview persist + cancel rollback. body = full staging JSON sans id.
    // userArmed-gated + debounced by the selector; NEVER auto-fired.
    patchEvapPackSelector: builder.mutation<
      IEvapPackRow,
      { id: number; data: IEvapPackRow }
    >({
      query: ({ id, data }) => {
        const body = { ...data };
        delete (body as { id?: unknown }).id; // legacy delete data.id
        return {
          url: `/api/v1.0/ahu/evap_packs/${id}`,
          method: 'PATCH',
          body,
        };
      },
    }),

    /**
     * `_fetchCalculate` (evap_pack.selector.js:960-1001) — READ-ONLY GET that
     * recomputes the ten derived outputs from section + picked media + airflow.
     * Legacy debounces it 300 ms and fires it on `airflow_actual` and
     * `master_media_id` changes (:543) and after a media smartlist pick (:958).
     * Query params are built by the caller so the eadb/eawb pair can be omitted
     * when unset, as legacy does.
     */
    getEvapPackCalculate: builder.query<IEvapCalculateResult, string>({
      query: (qs) => `/api/v1.0/ahu/evap_packs/calculate/?${qs}`,
    }),
  }),
  overrideExisting: false,
});

export const { usePatchEvapPackSelectorMutation } = evapPackSelectorApi;
